import { Fragment, useState } from 'react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import type { ShipmentStage } from '#/entities/shipment'
import { useOpenShipmentPreview } from '#/widgets/shipment-sidebar'
import { cn } from '#/shared/lib/utils'
import { ChevronDownIcon, ExternalLinkIcon } from '#/shared/ui/icons'
import { Skeleton } from '#/shared/ui/skeleton'

import { AVATAR_TONE_CLASS } from '../../lib/avatar-tone'
import type { ActiveLoad } from '../../model/conversation'

interface StageGroup {
  stage: ShipmentStage
  items: ActiveLoad[]
}

/** Group consecutive loads by stage; `loads` already arrive ordered by stage. */
function groupByStage(loads: ReadonlyArray<ActiveLoad>): StageGroup[] {
  const groups: StageGroup[] = []
  for (const load of loads) {
    const last = groups.at(-1)
    if (last?.stage === load.stage) last.items.push(load)
    else groups.push({ stage: load.stage, items: [load] })
  }
  return groups
}

/** Tiny stage heading ("Leads" / "Quotes" / "Orders") with a divider above all
 *  but the first group. */
function StageHeading({
  stage,
  first,
}: {
  stage: ShipmentStage
  first: boolean
}) {
  const { t } = useTranslation()
  return (
    <div
      className={cn(
        'px-4 pt-2 pb-1 text-2xs font-medium tracking-wide text-gray-400 uppercase select-none',
        !first && 'mt-1 border-t border-warm-100 pt-2',
      )}
    >
      {t(`phonePanel.conversation.loadStage.${stage}`)}
    </div>
  )
}

/** Stage-grouped rows shared by the inline and dropdown layouts. */
function StageGroupedLoads({
  loads,
  onSelect,
}: {
  loads: ReadonlyArray<ActiveLoad>
  onSelect: (load: ActiveLoad) => void
}) {
  return (
    <>
      {groupByStage(loads).map((group, index) => (
        <Fragment key={group.stage}>
          <StageHeading stage={group.stage} first={index === 0} />
          {group.items.map((load) => (
            <OrderRow key={load.id} load={load} onOpen={() => onSelect(load)} />
          ))}
        </Fragment>
      ))}
    </>
  )
}

function OrderRow({ load, onOpen }: { load: ActiveLoad; onOpen: () => void }) {
  const { t } = useTranslation()
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={t('phonePanel.conversation.openOrder', { code: load.code })}
      className="flex w-full cursor-pointer items-center gap-2 px-4 py-1.25 text-left transition-colors hover:bg-paper-avatar/60"
    >
      <span className="flex shrink-0 items-center gap-1 text-xs font-medium text-avatar-blue">
        #{load.code}
        <ExternalLinkIcon className="size-3.5" />
      </span>
      <span className="min-w-0 flex-1 truncate text-xs text-gray-500">
        {load.summary}
      </span>
      <span
        className={cn(
          'shrink-0 rounded px-1.5 py-0.5 text-2xs font-medium',
          AVATAR_TONE_CLASS[load.tone],
        )}
      >
        {load.statusLabel}
      </span>
    </button>
  )
}

/** Strip header: the active-load count and a show/hide toggle for the list. */
function StripHead({
  count,
  open,
  onToggle,
}: {
  count: number
  open: boolean
  onToggle: () => void
}) {
  const { t } = useTranslation()
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      className="flex w-full cursor-pointer items-center justify-between gap-2 px-4 pt-2 pb-1.5 text-left"
    >
      <span className="text-xs font-medium text-gray-500">
        {t('phonePanel.conversation.activeLoads', { count })}
      </span>
      <span className="flex items-center gap-1 text-xs font-medium text-avatar-blue">
        {open
          ? t('phonePanel.conversation.hideAll')
          : t('phonePanel.conversation.showAll')}
        <ChevronDownIcon
          className={cn(
            'size-3 transition-transform duration-200',
            open && 'rotate-180',
          )}
        />
      </span>
    </button>
  )
}

/** Shared frame for the strip's non-list states (skeleton, empty), matching the
 *  inline strip's frosted, bottom-bordered container. */
function StripFrame({ children }: { children: ReactNode }) {
  return (
    <div className="border-b border-warm-100 bg-white/70 py-1.5 backdrop-blur-md">
      {children}
    </div>
  )
}

/** Placeholder row shown while the customer's loads are being fetched. */
function OrdersStripSkeleton() {
  return (
    <StripFrame>
      <div className="flex items-center gap-2 px-4 py-1.25">
        <Skeleton className="h-3.5 w-12 shrink-0" />
        <Skeleton className="h-3 flex-1" />
        <Skeleton className="h-4 w-14 shrink-0 rounded" />
      </div>
    </StripFrame>
  )
}

/** Shown when a known customer has no loads yet. */
function OrdersStripEmpty() {
  const { t } = useTranslation()
  return (
    <StripFrame>
      <p className="px-4 py-0.5 text-xs text-gray-400">
        {t('phonePanel.conversation.noActiveLoads')}
      </p>
    </StripFrame>
  )
}

/** A few orders are always visible, so render them inline below the header. */
const INLINE_LIMIT = 2

/**
 * Header strip of the counterparty's active orders. With a short list (≤2) the
 * orders show inline directly; beyond that it collapses to a count row with a
 * show/hide toggle and a floating dropdown.
 *
 * The always-visible part sits in normal flow so it's measured into the header
 * inset and the thread reserves space for it — otherwise the first date pill
 * slides under it. Only the expandable list floats, dropping over the thread
 * directly beneath the count row.
 *
 * Renders nothing when no CRM customer is linked; otherwise shows a skeleton
 * while loads load and an empty state when the customer has none.
 */
export function OrdersStrip({
  loads,
  isLoading,
  hasCustomer,
}: {
  loads: ReadonlyArray<ActiveLoad>
  isLoading: boolean
  hasCustomer: boolean
}) {
  const openPreview = useOpenShipmentPreview()
  const [open, setOpen] = useState(false)

  // No customer linked → no loads concept to show.
  if (!hasCustomer) return null
  if (isLoading) return <OrdersStripSkeleton />
  if (loads.length === 0) return <OrdersStripEmpty />

  const select = (load: ActiveLoad) => {
    setOpen(false)
    openPreview(load.stage, load.id)
  }

  // ≤2 loads show inline, ungrouped — too few to need stage headings.
  if (loads.length <= INLINE_LIMIT) {
    return (
      <div
        data-coach="orders"
        className="border-b border-warm-100 bg-white/70 py-1.5 backdrop-blur-md"
      >
        {loads.map((load) => (
          <OrderRow key={load.id} load={load} onOpen={() => select(load)} />
        ))}
      </div>
    )
  }

  return (
    // Only the count row is in normal flow (measured into the header inset). The
    // expandable list floats below it as an absolute dropdown over the thread, so
    // opening it doesn't grow the header and shift the chat layout. The frost sits
    // on the count row and the dropdown separately — never on the wrapper — since
    // a backdrop-filter nested inside another cancels the child's blur.
    <div data-coach="orders" className="relative">
      <div className="bg-white/70 backdrop-blur-md">
        <StripHead
          count={loads.length}
          open={open}
          onToggle={() => setOpen((value) => !value)}
        />
      </div>
      {open && (
        <div className="animate-slide-down absolute inset-x-0 top-full z-10 flex max-h-72 flex-col overflow-y-auto border-b border-warm-100 bg-white/70 pb-1.5 shadow-popover backdrop-blur-md">
          <StageGroupedLoads loads={loads} onSelect={select} />
        </div>
      )}
    </div>
  )
}
